import { SignedIn, SignedOut, SignIn, UserButton, useAuth, useUser } from "@clerk/clerk-react";
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";
import { setTokenProvider, apiPost } from "./services/api";
import { warmupBackend } from "./services/warmupService";
import { startKeepAlive, stopKeepAlive } from "./services/keepAliveService";
import MyLeaguesPage from "./pages/MyLeaguesPage";
import LeagueOverviewPage from "./pages/LeagueOverviewPage";
import GameweekPicksPage from "./pages/GameweekPicksPage";
import LeagueTablePage from "./pages/LeagueTablePage";
import LeagueHistoryPage from "./pages/LeagueHistoryPage";
import LeagueHistoryGameweekDetailPage from "./pages/LeagueHistoryGameweekDetailPage";
import AdminPage from "./pages/AdminPage";
import DashboardPage from "./pages/DashboardPage";
import JoinLeaguePage from "./pages/JoinLeaguePage";
import SettingsPage from "./pages/SettingsPage";

// ---------------------------------------------------------------------------
// localStorage helpers for userId caching
// ---------------------------------------------------------------------------
const CACHED_USER_ID_KEY = "prem_picks_user_id";

function getCachedUserId() {
  try {
    const cached = localStorage.getItem(CACHED_USER_ID_KEY);
    const id = cached ? parseInt(cached, 10) : null;
    // Validate it's a positive integer
    return Number.isInteger(id) && id > 0 ? id : null;
  } catch (error) {
    console.warn("Failed to read cached userId:", error);
    return null;
  }
}

function setCachedUserId(id) {
  try {
    if (Number.isInteger(id) && id > 0) {
      localStorage.setItem(CACHED_USER_ID_KEY, String(id));
    }
  } catch (error) {
    console.warn("Failed to cache userId:", error);
  }
}

function clearCachedUserId() {
  try {
    localStorage.removeItem(CACHED_USER_ID_KEY);
  } catch (error) {
    console.warn("Failed to clear cached userId:", error);
  }
}

const ADMIN_USER_IDS = (process.env.REACT_APP_ADMIN_USER_IDS || "")
  .split(",")
  .map((s) => s.trim())
  .filter(Boolean);

// ---------------------------------------------------------------------------
// Loading skeleton screen shown during warmup
// ---------------------------------------------------------------------------
function WarmupSkeleton() {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-slate-100 p-4">
      <div className="w-full max-w-md space-y-4">
        <div className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
          <div className="h-8 w-48 animate-pulse rounded bg-slate-200" />
          <div className="mt-3 h-4 w-72 animate-pulse rounded bg-slate-100" />
        </div>
        <div className="rounded-3xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
          <div className="h-12 animate-pulse rounded-2xl bg-slate-100" />
          <div className="mt-3 h-12 animate-pulse rounded-2xl bg-slate-100" />
        </div>
        <p className="text-center text-sm font-medium text-slate-600">Loading...</p>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Simple layout for pages that don't need full app chrome
// ---------------------------------------------------------------------------
function SimpleLayout({ children }) {
  return (
    <div className="min-h-screen bg-slate-100 text-slate-900 flex flex-col">
      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8 flex-1 w-full">{children}</main>

      <footer className="border-t border-slate-200 bg-white mt-12 sm:mt-16">
        <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
          <p className="text-xs text-slate-500 text-center">
            Found an issue, bug, or have a feature suggestion?{" "}
            <a
              href="mailto:sami@samiapp.uk"
              className="font-medium text-slate-700 hover:text-emerald-600 transition"
            >
              Email sami@samiapp.uk
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mobile nav drawer — closes automatically on route change
// ---------------------------------------------------------------------------
function MobileNav({ isAdmin, open, onClose }) {
  const location = useLocation();

  useEffect(() => {
    onClose();
  }, [location.pathname, onClose]);

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-20 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="fixed inset-x-0 top-0 z-30 rounded-b-3xl bg-white px-6 pb-6 pt-4 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <p className="text-sm font-semibold text-slate-500">Menu</p>
          <button
            onClick={onClose}
            className="rounded-full p-2 text-slate-500 transition hover:bg-slate-100"
            aria-label="Close menu"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <nav className="flex flex-col gap-1">
          <Link to="/" className="rounded-2xl px-4 py-3 text-base font-medium text-slate-700 transition hover:bg-slate-100">
            Dashboard
          </Link>
          <Link to="/leagues" className="rounded-2xl px-4 py-3 text-base font-medium text-slate-700 transition hover:bg-slate-100">
            My Leagues
          </Link>
          <Link to="/settings" className="rounded-2xl px-4 py-3 text-base font-medium text-slate-700 transition hover:bg-slate-100">
            Settings
          </Link>
          {isAdmin && (
            <Link to="/admin" className="rounded-2xl px-4 py-3 text-base font-medium text-amber-600 transition hover:bg-amber-50">
              Admin
            </Link>
          )}
        </nav>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Shared layout
// ---------------------------------------------------------------------------
function AppLayout({ children }) {
  const { user } = useUser();
  const isAdmin = user && ADMIN_USER_IDS.includes(user.id);
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900 flex flex-col">
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3 sm:gap-6">
            <button
              className="flex sm:hidden items-center justify-center rounded-full p-2 text-slate-600 transition hover:bg-slate-100"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            <Link to="/" className="hover:opacity-80 transition-opacity">
            <div>
              <p className="hidden text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600 sm:block">
                Premier League Predictor
              </p>
              <h1 className="text-xl font-bold tracking-tight">Prem Picks</h1>
            </div>
          </Link>

            <nav className="hidden items-center gap-1 sm:flex">
              <Link to="/" className="rounded-full px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900">
                Dashboard
              </Link>
              <Link to="/leagues" className="rounded-full px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900">
                My Leagues
              </Link>
              <Link to="/settings" className="rounded-full px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900">
                Settings
              </Link>
              {isAdmin && (
                <Link to="/admin" className="rounded-full px-3 py-1.5 text-sm font-medium text-amber-600 transition hover:bg-amber-50 hover:text-amber-700">
                  Admin
                </Link>
              )}
            </nav>
          </div>

          <UserButton />
        </div>
      </header>

      <MobileNav isAdmin={isAdmin} open={menuOpen} onClose={closeMenu} />

      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8 flex-1 w-full">{children}</main>

      <footer className="border-t border-slate-200 bg-white mt-12 sm:mt-16">
        <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
          <p className="text-xs text-slate-500 text-center">
            Found an issue, bug, or have a feature suggestion?{" "}
            <a
              href="mailto:sami@samiapp.uk"
              className="font-medium text-slate-700 hover:text-emerald-600 transition"
            >
              Email sami@samiapp.uk
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Routes — token provider registered synchronously on every render so it is
// always set before any child component fires a request.
// ---------------------------------------------------------------------------
function AppRoutes() {
  // Use cached userId if available, otherwise null
  const [appUserId, setAppUserId] = useState(() => getCachedUserId());
  const [backendReady, setBackendReady] = useState(false);
  const { user, isLoaded } = useUser();
  const isAdmin = user && ADMIN_USER_IDS.includes(user.id);
  const syncedRef = useRef(false);
  const warmedUpRef = useRef(false);
  const [syncRetryCount, setSyncRetryCount] = useState(0);

  // Warm up the backend on first load to hide cold start
  useEffect(() => {
    if (warmedUpRef.current) return;
    warmedUpRef.current = true;

    console.log("Starting backend warmup...");
    warmupBackend().then(() => {
      console.log("Backend warmup complete, setting backendReady");
      setBackendReady(true);
    }).catch((error) => {
      console.error("Backend warmup error:", error);
      setBackendReady(true); // Set to true anyway to let app continue
    });
  }, []);

  // Sync the Clerk user with our backend once, as soon as Clerk is ready.
  // Doing this at the Routes level means every page (including Dashboard on
  // direct load) gets a userId without having to visit /leagues first.
  useEffect(() => {
    // Clear cache and stop keep-alive when user logs out
    if (isLoaded && !user) {
      clearCachedUserId();
      setAppUserId(null);
      syncedRef.current = false;
      stopKeepAlive();
      return;
    }

    if (!backendReady || !isLoaded || !user || syncedRef.current) return;

    syncedRef.current = true;

    async function syncUser() {
      try {
        console.log("Syncing user with Clerk ID:", user.id);
        const data = await apiPost("/Users/sync", {
          clerkUserId: user.id,
          email: user.primaryEmailAddress?.emailAddress || "",
        });
        console.log("Sync response:", data);
        // Backend returns 'id' (lowercase)
        const id = data?.id ?? data?.Id ?? data?.userId ?? data?.appUserId ?? null;
        console.log("Extracted userId:", id, "Type:", typeof id);
        if (Number.isInteger(id) && id > 0) {
          console.log("Setting appUserId to:", id);
          setCachedUserId(id);
          setAppUserId(id);
          // Start keep-alive to keep the backend warm for pick reminders
          startKeepAlive();
        } else {
          console.warn("Invalid userId from sync:", id, "Type:", typeof id);
        }
      } catch (error) {
        // Non-fatal — individual pages surface their own errors if needed.
        console.error("User sync error:", error);
        syncedRef.current = false; // allow a retry on next render if it failed
        window.setTimeout(() => {
          setSyncRetryCount((current) => (current < 5 ? current + 1 : current));
        }, 3000);
      }
    }

    syncUser();
  }, [backendReady, isLoaded, user, syncRetryCount]);

  const handleUserLoaded = useCallback((id) => setAppUserId(id), []);

  // Show skeleton UI only while backend is warming up
  // Don't wait for appUserId here - let pages handle missing userId gracefully
  // User sync happens in parallel and will populate userId shortly
  if (!backendReady) {
    console.log("Showing skeleton - backendReady:", backendReady);
    return <WarmupSkeleton />;
  }
  
  console.log("Backend ready, rendering routes with appUserId:", appUserId);

  return (
    <Routes>
      <Route path="/" element={<AppLayout><DashboardPage userId={appUserId} /></AppLayout>} />

      <Route
        path="/leagues"
        element={<AppLayout><MyLeaguesPage onUserLoaded={handleUserLoaded} /></AppLayout>}
      />

      <Route
        path="/leagues/:leagueId"
        element={<AppLayout><LeagueOverviewPage userId={appUserId} /></AppLayout>}
      />

      <Route
        path="/leagues/:leagueId/picks"
        element={<AppLayout><GameweekPicksPage userId={appUserId} /></AppLayout>}
      />

      <Route
        path="/leagues/:leagueId/table"
        element={<AppLayout><LeagueTablePage userId={appUserId} /></AppLayout>}
      />

      <Route
        path="/leagues/:leagueId/history"
        element={<AppLayout><LeagueHistoryPage userId={appUserId} /></AppLayout>}
      />

      <Route
        path="/leagues/:leagueId/history/gameweek/:gameweekId"
        element={<AppLayout><LeagueHistoryGameweekDetailPage userId={appUserId} /></AppLayout>}
      />

      <Route
        path="/settings"
        element={<AppLayout><SettingsPage /></AppLayout>}
      />

      {isAdmin && (
        <Route
          path="/admin"
          element={<AppLayout><AdminPage /></AppLayout>}
        />
      )}

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------
function App() {
  const { getToken } = useAuth();

  // Register token provider at root level so it's available for all routes,
  // including /join which is outside <SignedIn>
  setTokenProvider(getToken);

  return (
    <BrowserRouter>
      <Routes>
        {/* Join page works for both signed in and signed out users */}
        <Route path="/join/:inviteCode" element={<SimpleLayout><JoinLeaguePage /></SimpleLayout>} />

        {/* All other routes */}
        <Route
          path="*"
          element={
            <>
              <SignedOut>
                <div className="flex min-h-[100dvh] items-center justify-center bg-slate-100 p-4">
                  <div className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-slate-200">
                    <div className="px-6 pb-2 pt-6">
                      <h1 className="text-3xl font-bold tracking-tight">Prem Picks</h1>
                      <p className="mt-2 text-sm text-slate-600">
                        Predict scores, compete with friends, and climb the table.
                      </p>
                    </div>
                    <SignIn routing="hash" />
                  </div>
                </div>
              </SignedOut>

              <SignedIn>
                <AppRoutes />
              </SignedIn>
            </>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
